import { useState, useEffect, useRef, use } from "react";
import Sidebar from "../../../../../frontend/DesignSystem/Sidebar";
import Breadcrumb from "../../../../../frontend/DesignSystem/Breadcrumb/Breadcrumb";
import AddEditButton from "../Buttons/AddEditButton";
import Pagination from "./Pagination";
import "../../../../../css/CashflowModeller2/Dashboard/CfmDashboard.css";
import { useNavigate } from "react-router-dom";
import { useUserEmail } from "../../../../../context/UserEmailContext";
import { useUserRole } from "../../../../../context/UserRoleContext";
import { useCfmContext } from "../../Context/CfmContext";
import ButtonSmall from "../../../../../frontend/DesignSystem/Buttons/ButtonSmall";
import { ReactComponent as CloseIcon } from "../../../../../assets/icons/close.svg";
import { PDFViewer } from "@react-pdf/renderer";
import CfmReport from "./../Reports/CFMReport";
import "../../../../../css/CashflowModeller2/Reports/CfmReport.css";
import PensionCalculations from "../../Context/HelperFunctions/PensionCalculations";
import InvestmentCalculations from "../../Context/HelperFunctions/InvestmentCalculations";
import IncomeExpenditureCalculations from "../../Context/HelperFunctions/IncomeExpenditureCalculations";
import { useUserPermissions } from "../../../../../context/UserPermissionsContext";
import { getAllCashflowClientsForAdmin } from "../../Helpers/GetAllCashflowClientsForAdmin";
import { getCashflowClientsForUser } from "../../Helpers/GetCashflowClientsForUser";
import { getCashflowClientsForUserAndAdvisers } from "../../Helpers/GetCashflowClientsForAdmin";
import { useClientActions } from "../../Hooks/UseClientActions";
import { useClientMeta } from "../../Hooks/UseClientMeta";
import { Helmet } from "react-helmet-async";
import { useAdminAdviserMails } from "../../../../../context/AdminAdviserEmailsContext";
import { useAdviserStatus } from "../../../../../context/AdviserStatusContext";
import { useAdminStatus } from "../../../../../context/AdminStatusContext";
import { getCashflowClientsForAdviser } from "../../Helpers/GetCashflowClientsAdvisers";
/**
 * CfmDashboard is the main dashboard component for the Cashflow Modeller tool.
 *
 * This component displays a paginated list of clients, allows users to view, preview (PDF), and delete clients,
 * and manages the generation of various financial charts for reporting. It handles user permissions,
 * context management, and conditional rendering based on user roles and data loading state.
 *
 * Features:
 * - Fetches and displays clients based on user role and permissions.
 * - Supports pagination and items-per-page selection.
 * - Allows users to view client details and generate PDF previews with financial charts.
 * - Handles client deletion with confirmation modal.
 * - Manages branding and regulatory text based on client ownership.
 * - Restricts access to the dashboard based on user role and permissions.
 *
 * Hooks & Context:
 * - Uses custom hooks for user info, permissions, and client actions.
 * - Uses context for managing Cashflow Modeller state.
 *
 * @component
 * @returns {JSX.Element} The rendered dashboard component for Cashflow Modeller.
 */
const CfmDashboard = () => {
  PensionCalculations();
  InvestmentCalculations();
  IncomeExpenditureCalculations();

  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { userPermissions } = useUserPermissions();
  const { adminAdviserMails } = useAdminAdviserMails();
  const navigate = useNavigate();
  const { dispatch, state } = useCfmContext();
  const { adviserStatus } = useAdviserStatus();
  const { adminStatus } = useAdminStatus();

  const [clients, setClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => {
    // Try getting it from localStorage or default to page 1
    const savedPage = parseInt(localStorage.getItem("cfmCurrentPage"), 10);
    return isNaN(savedPage) ? 1 : savedPage;
  });

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewClientState, setPreviewClientState] = useState(null);

  const [pensionChartImages, setPensionChartImages] = useState({});
  const [investmentChartImages, setIvestmentChartImages] = useState({});
  const [incExpdChartImages, setIncExpdChartImages] = useState({});
  const [surDefChartImages, setSurDefChartImages] = useState({});
  const [stackedPenChartImages, setStackedPenChartImages] = useState({});
  const [stackedInvChartImages, setStackedInvChartImages] = useState({});
  const [combinedIncExpdChartImages, setCombinedIncExpdChartImages] = useState(
    {},
  );
  const [combinedSurDefChartImages, setCombinedSurDefChartImages] = useState(
    {},
  );

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  const { handleClient, handleDelete } = useClientActions();
  const { tradingName, primaryColor, logo, userName } = useClientMeta();
  const cfmAdminkey = userPermissions.includes("CFM Admin");

  const totalPages = Math.ceil(clients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = clients.slice(startIndex, startIndex + itemsPerPage);
  const [includeTables, setIncludeTables] = useState(true);

  const [showDeletedToast, setShowDeletedToast] = useState(false);
  const hideDeletedToastRef = useRef(null);

  const breadcrumbItems = [
    { label: "Tools & Calculators", to: "/portal/tools-calculators" },
    { label: "Cashflow Modeller" },
  ];

  const isAdviserAdmin = (userRole || "").toLowerCase() === "administrators";

  // For team-scope checks (delete/filters etc.)
  const teamScopeEmails = isAdviserAdmin
    ? [userEmail, ...(adminAdviserMails || [])]
    : [];

  const showDeleted = () => {
    setShowDeletedToast(true);
    if (hideDeletedToastRef.current) clearTimeout(hideDeletedToastRef.current);
    hideDeletedToastRef.current = setTimeout(
      () => setShowDeletedToast(false),
      2000,
    );
  };

  useEffect(() => {
    return () => {
      if (hideDeletedToastRef.current)
        clearTimeout(hideDeletedToastRef.current);
    };
  }, []);

  // clear preview images
  const resetPreviewState = () => {
    setPreviewClientState(null);
    setPensionChartImages({});
    setIvestmentChartImages({});
    setIncExpdChartImages({});
    setSurDefChartImages({});
    setStackedPenChartImages({});
    setStackedInvChartImages({});
    setCombinedIncExpdChartImages({});
    setCombinedSurDefChartImages({});
    setPreviewVisible(false);
  };

  useEffect(() => {
    if (
      // (userRole === "Staff" &&
      //   (userPermissions?.includes("Compliance") ||
      //     userEmail === "zachary.bawa@rosemountfs.co.uk" ||
      //     // userEmail === "jacko.lam@rosemountfs.co.uk" ||
      //     userEmail === "calum.anderson@rosemountfs.co.uk" ||
      //     userEmail === "callum.wilkinson@rosemountfs.co.uk" ||
      //     userEmail === "shashank.bedade@rosemountfs.co.uk")) ||
      // adviserStatus.includes("CFM") ||
      // adminStatus.includes("NSCFM")
      (userRole === "Staff" &&
        (userPermissions?.includes("Compliance") ||
          userPermissions?.includes("Paraplanning") ||
          userPermissions?.includes("Compliance Support"))) ||
      adviserStatus.includes("FP") ||
      adviserStatus.includes("NAP") ||
      adminStatus.includes("NSFP")
    ) {
    } else navigate("/members-hub");
  }, []);

  /**
   * Handles the action to view a client's details.
   * Invokes the handleClient function with the provided entry and the "view" mode.
   *
   * @async
   * @function
   * @param {Object} entry - The client entry object to be viewed.
   * @returns {Promise<void>} A promise that resolves when the client view action is complete.
   */
  const handleViewClient = async (entry) => {
    await handleClient(entry, "view");
  };

  /**
   * Fetches cashflow clients from Firestore based on the user's role and email.
   * If the user has an admin key, retrieves all clients for admin;
   * otherwise, retrieves clients specific to the user's role and email.
   * Updates the clients state and loading state accordingly.
   *
   * @async
   * @function fetchClientsFromFirestore
   * @returns {Promise<void>} Resolves when clients are fetched and state is updated.
   */
  const fetchClientsFromFirestore = async () => {
    if (!userRole || !userEmail) return;
    const entries = cfmAdminkey
      ? await getAllCashflowClientsForAdmin()
      : userRole === "Administrators"
        ? await getCashflowClientsForUserAndAdvisers({
            userRole,
            userEmail,
            adviserEmails: adminAdviserMails || [],
          })
        : userRole === "Advisers"
          ? await getCashflowClientsForAdviser(userRole, userEmail)
          : await getCashflowClientsForUser(userRole, userEmail);

    // Build a set of all scenario child ids referenced by any parent doc
    const childIds = new Set(
      entries.flatMap((e) =>
        Array.isArray(e?.scenarios)
          ? e.scenarios.map((s) => s.id).filter(Boolean)
          : [],
      ),
    );

    // Normalize truthy flags in case they're strings like "true"
    const isTrue = (v) => v === true || v === "true" || v === 1 || v === "1";
    // console.log("entries sample", entries.slice(0, 5));

    // Keep only parents:
    // - not explicitly marked as scenario
    // - no parentDocId (null/undefined/"")
    // - id is NOT referenced in any scenarios array
    let parentsOnly = entries.filter((e) => {
      const looksLikeScenario = isTrue(e?.isScenario) || !!e?.parentDocId;
      const referencedAsChild = childIds.has(e?.id);
      return !looksLikeScenario && !referencedAsChild;
    });

    // Fallback de-dupe for very old data (if needed)
    if (parentsOnly.length === 0) {
      const byKey = new Map();
      for (const e of entries) {
        const key = `${e.createdBy || ""}|${(e.name || "").trim()}`;
        const ts = new Date(e.rawDate || e.date || 0).getTime();
        const curr = byKey.get(key);
        if (!curr || ts < curr._ts) byKey.set(key, { ...e, _ts: ts });
      }
      parentsOnly = Array.from(byKey.values()).map(({ _ts, ...rest }) => rest);
    }

    const parseDMY = (s) => {
      if (!s) return 0;
      const [d, m, y] = s.split("/");
      return new Date(+y, +m - 1, +d).getTime();
    };

    parentsOnly.sort((a, b) => {
      const ta = parseDMY(a.updatedOn) || parseDMY(a.date);
      const tb = parseDMY(b.updatedOn) || parseDMY(b.date);
      return tb - ta; // newest first
    });

    setClients(parentsOnly);
    setLoading(false);
  };

  // useEffect(() => {
  //   console.log(adminAdviserMails);
  // }, [adminAdviserMails]);

  /**
   * Handles the change of the current page in the dashboard.
   * Updates the state with the new page number and persists it in localStorage.
   *
   * @param {number} page - The page number to set as the current page.
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
    localStorage.setItem("cfmCurrentPage", page.toString());
  };

  useEffect(() => {
    fetchClientsFromFirestore();
  }, [userRole, userEmail, userPermissions]);

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Cashflow Modeller</title>
        <meta
          name="description"
          content="Rosemount Financial Solutions (IFA) Ltd - Adviser Portal"
        />
      </Helmet>
      <Sidebar />
      <div className="Cfm-Dashboard-Container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="Cfm-Dashboard-Header">
          <h2>Cashflow Modeller</h2>
          <AddEditButton
            buttonText="New Client"
            onClick={() => {
              localStorage.removeItem("cfmContextData");
              dispatch({ type: "RESET_CONTEXT" });
              dispatch({ type: "SET_DOC_ID", payload: null });
              setTimeout(() => navigate("/cashflow-modeller"), 50);
            }}
          />
        </div>
        {loading ? (
          <div className="Initialising-Overlay">
            <div className="Spinner" />
          </div>
        ) : clients.length === 0 ? (
          <div className="Cfm-Dashboard-Header">
            <div className="Content">No clients found.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="Cfm-Dashboard-table">
              <thead>
                <tr>
                  <th className="Display"></th>
                  <th className="Display">Client Name</th>
                  <th className="Display">Timestamps</th>
                  {/* <th className="Display">Download</th> */}
                  <th className="Display">Delete</th>
                  {/* {cfmAdminkey && (
                    <>
                      <th className="Display">Created By</th>
                      <th className="Display">Trading Name</th>
                      <th className="Display">Primary Color</th>
                    </>
                  )} */}
                  <th className="Display">View</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClients.map((entry, index) => {
                  // console.log(entry);
                  return (
                    <tr key={index}>
                      <td className="Input">
                        <div onClick={() => handleViewClient(entry)}>
                          {startIndex + index + 1}.
                        </div>
                      </td>
                      <td className="Input">
                        <div
                          onClick={() => handleViewClient(entry)}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            cursor: "pointer",
                            gap: "4px",
                          }}
                        >
                          <div>{entry.name}</div>
                          {cfmAdminkey && (
                            <div className="Content-Small">
                              {entry.createdBy}
                            </div>
                          )}
                        </div>
                      </td>
                      <td
                        className="Content-Small"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          cursor: "pointer",
                          gap: "4px",
                        }}
                        onClick={() => handleViewClient(entry)}
                      >
                        <div>Updated On: {entry.updatedOn || ""}</div>
                        <div>Created On: {entry.date || ""}</div>
                      </td>
                      {/* <td
                      className="Input"
                      onClick={() => {
                        // console.log();
                        handlePreviewClient(entry);
                      }}
                    >
                      Download
                    </td> */}
                      <td
                        className="Input"
                        style={{
                          cursor:
                            entry.createdBy === userEmail
                              ? "pointer"
                              : "not-allowed",
                        }}
                        onClick={() => {
                          {
                            if (entry.createdBy === userEmail) {
                              setShowDeleteConfirm(true);
                              setClientToDelete(entry.id);
                            }
                          }
                          // handleDeleteClient(entry.id)
                        }}
                      >
                        <div>Delete</div>
                      </td>

                      {/* {cfmAdminkey && (
                      <>
                      <td className="Input">{entry.createdBy}</td>
                      <td className="Input">{entry.tradingName}</td>
                      <td className="Input">{entry.regulatoryText}</td>
                      </>
                    )} */}

                      <td
                        className="Input"
                        onClick={() => handleViewClient(entry)}
                      >
                        <div>View</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="pagination-wrapper">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={(val) => {
                  setItemsPerPage(val);
                  setCurrentPage(1);
                  localStorage.setItem("cfmCurrentPage", "1");
                }}
              />
            </div>
          </div>
        )}
      </div>
      {showDeleteConfirm && (
        <div className="confirmation-backdrop">
          <div className="confirmation-modal">
            <CloseIcon
              className="close-icon"
              onClick={() => {
                setShowDeleteConfirm(false);
                setClientToDelete(null);
              }}
            />

            <div className="Content-Large">
              Are you sure you want to delete this client?
            </div>
            <div className="confirmation-buttons">
              <ButtonSmall
                buttonText="Yes"
                onClick={() => {
                  const entry = clients.find((c) => c.id === clientToDelete);
                  handleDelete(entry, (deletedId) => {
                    setClients((prev) =>
                      prev.filter((client) => client.id !== deletedId),
                    );
                  });
                  setShowDeleteConfirm(false);
                  setClientToDelete(null);
                  showDeleted();
                }}
              />

              <ButtonSmall
                buttonText="No"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setClientToDelete(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showDeletedToast && (
        <div className="DeletedToast">
          <div className="dialog-overlay">
            <div className="dialog-box ">
              <div className="confirm-delete-dialog">
                <div className="Title">Client deleted successfully.</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewVisible && (
        <div
          className="pdf-preview-modal-backdrop"
          onClick={() => {
            setPreviewVisible(false);
            localStorage.removeItem("cfmContextData");
            dispatch({ type: "RESET_CONTEXT" });
          }}
        >
          {/* <IncomeExpenditureGraph
            selectedPerson={state.details?.[0]?.name || ""}
            onChartExport={(image) => setChartImage(image)}
          /> */}
          <div className="pdf-preview-modal">
            <PDFViewer style={{ width: "100%", height: "90vh" }}>
              <CfmReport
                state={state}
                tradingName={
                  previewClientState?.createdBy === userEmail
                    ? tradingName // own client: use your context branding
                    : previewClientState?.tradingName
                }
                primaryColor={
                  previewClientState?.createdBy === userEmail
                    ? primaryColor
                    : previewClientState?.primaryColor
                }
                logo={
                  previewClientState?.createdBy === userEmail
                    ? logo // your context logo
                    : previewClientState?.logo
                }
                userName={
                  previewClientState?.createdBy === userEmail
                    ? userName
                    : previewClientState?.createdBy || userEmail
                }
                regulatoryText={
                  previewClientState?.createdBy === userEmail
                    ? previewClientState?.regulatoryText ||
                      "Rosemount Financial Solutions (IFA) Ltd is authorised and regulated by the Financial Conduct Authority (FCA). Entered on the FCA Register (https://register.fca.org.uk/) under reference 535515. Rosemount Financial Solutions (IFA) Ltd is registered in England and Wales, Registered No. 05668905, Registered Address Rosemount House, 2 – 4 Chequers Road, Basingstoke, RG21 7PU."
                    : previewClientState?.regulatoryText
                }
                pensionChartImages={pensionChartImages}
                investmentChartImages={investmentChartImages}
                incExpdChartImages={incExpdChartImages}
                surDefChartImages={surDefChartImages}
                stackedPenChartImages={stackedPenChartImages}
                stackedInvChartImages={stackedInvChartImages}
                combinedIncExpdChartImages={combinedIncExpdChartImages}
                combinedSurDefChartImages={combinedSurDefChartImages}
                includeTables={includeTables}
              />
            </PDFViewer>
          </div>
        </div>
      )}
    </div>
  );
};

export default CfmDashboard;
